import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, apiClient } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, Trash2, ChevronDown, ChevronUp, 
  Users, Sparkles, Clock, Pin, PinOff, Image, X, Loader2, Trophy, Link, Plus
} from 'lucide-react';
import GroupMilestones from '../components/GroupMilestones';

const Community = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('notes'); // 'notes' or 'milestones'
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [posting, setPosting] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [commentInputs, setCommentInputs] = useState({});
  const [postingComment, setPostingComment] = useState(null);
  const [noteComments, setNoteComments] = useState({});
  const [loadingComments, setLoadingComments] = useState(new Set());
  const [pinningNote, setPinningNote] = useState(null);
  
  // Image upload state
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  
  // Link state
  const [links, setLinks] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  
  // Reaction tooltip state
  const [reactionNames, setReactionNames] = useState({});
  const [loadingReactions, setLoadingReactions] = useState({});

  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchNotes();

    // Keep the feed fresh while the user has the page open.
    // Poll every 30s, and also re-fetch the moment the tab/app regains focus
    // so iOS PWA users see new admin posts without closing and reopening.
    const POLL_MS = 30 * 1000;
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchNotes({ silent: true });
      }
    }, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchNotes({ silent: true });
      }
    };
    const onFocus = () => fetchNotes({ silent: true });

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const fetchNotes = async ({ silent } = {}) => {
    try {
      const res = await apiClient.get('/community-notes');
      setNotes(res.data);
    } catch (err) {
      console.error('Failed to fetch community notes:', err);
      // Don't toast on silent (background) refreshes — only on the initial load
      if (!silent) toast.error('Failed to load community notes');
    } finally {
      setLoading(false);
    }
  };

  const createNote = async () => {
    if (!newNote.trim() && !imageUrl) {
      toast.error('Please write something or add an image to share!');
      return;
    }

    setPosting(true);
    try {
      const res = await apiClient.post('/community-notes', { 
        content: newNote,
        image_url: imageUrl || null,
        links: links.length > 0 ? links : null
      });
      setNotes([res.data, ...notes]);
      setNewNote('');
      setImageUrl('');
      setLinks([]);
      setShowLinkForm(false);
      toast.success('Note shared with the community!');
      // Dispatch event to notify other components about new community content
      window.dispatchEvent(new Event('communityUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post note');
    } finally {
      setPosting(false);
    }
  };

  const addLink = () => {
    if (!newLinkUrl.trim() || !newLinkLabel.trim()) {
      toast.error('Please enter both URL and label');
      return;
    }
    // Basic URL validation
    let url = newLinkUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    setLinks([...links, { url, label: newLinkLabel.trim() }]);
    setNewLinkUrl('');
    setNewLinkLabel('');
    setShowLinkForm(false);
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiClient.post('/upload/community-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = `${baseUrl}${res.data.url}`;
      setImageUrl(fullUrl);
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const fetchReactionNames = async (noteId) => {
    if (reactionNames[noteId] || loadingReactions[noteId]) return;
    
    setLoadingReactions(prev => ({ ...prev, [noteId]: true }));
    try {
      const res = await apiClient.get(`/community-notes/${noteId}/reactions`);
      const names = res.data.map(r => r.user_name || 'Unknown');
      setReactionNames(prev => ({ ...prev, [noteId]: names }));
    } catch (err) {
      console.error('Failed to fetch reactions:', err);
    } finally {
      setLoadingReactions(prev => ({ ...prev, [noteId]: false }));
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await apiClient.delete(`/community-notes/${noteId}`);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const togglePinNote = async (noteId, currentlyPinned) => {
    setPinningNote(noteId);
    try {
      const res = await apiClient.post(`/admin/community-notes/${noteId}/pin`);
      setNotes(notes.map(note => {
        if (note.id === noteId) {
          return { ...note, is_pinned: res.data.is_pinned };
        }
        return note;
      }));
      toast.success(res.data.is_pinned ? 'Note pinned to top!' : 'Note unpinned');
      // Refetch to get correct ordering
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pin note');
    } finally {
      setPinningNote(null);
    }
  };

  const toggleReaction = async (noteId) => {
    try {
      const res = await apiClient.post(`/community-notes/${noteId}/react`);
      setNotes(notes.map(note => {
        if (note.id === noteId) {
          return {
            ...note,
            user_reacted: res.data.reacted,
            reactions_count: note.reactions_count + (res.data.reacted ? 1 : -1)
          };
        }
        return note;
      }));
    } catch (err) {
      toast.error('Failed to react');
    }
  };

  const toggleExpand = async (noteId) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
      // Fetch comments if not already loaded
      if (!noteComments[noteId]) {
        await fetchComments(noteId);
      }
    }
    setExpandedNotes(newExpanded);
  };

  const fetchComments = async (noteId) => {
    setLoadingComments(prev => new Set([...prev, noteId]));
    try {
      const res = await apiClient.get(`/community-notes/${noteId}/comments`);
      setNoteComments(prev => ({ ...prev, [noteId]: res.data }));
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoadingComments(prev => {
        const next = new Set(prev);
        next.delete(noteId);
        return next;
      });
    }
  };

  const addComment = async (noteId) => {
    const content = commentInputs[noteId]?.trim();
    if (!content) {
      toast.error('Please write a comment');
      return;
    }

    setPostingComment(noteId);
    try {
      const res = await apiClient.post(`/community-notes/${noteId}/comments`, { content });
      setNoteComments(prev => ({
        ...prev,
        [noteId]: [...(prev[noteId] || []), res.data]
      }));
      setCommentInputs(prev => ({ ...prev, [noteId]: '' }));
      // Update comment count in notes
      setNotes(notes.map(note => {
        if (note.id === noteId) {
          return { ...note, comments_count: note.comments_count + 1 };
        }
        return note;
      }));
      toast.success('Comment added!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    } finally {
      setPostingComment(null);
    }
  };

  const deleteComment = async (noteId, commentId) => {
    try {
      await apiClient.delete(`/community-notes/comments/${commentId}`);
      setNoteComments(prev => ({
        ...prev,
        [noteId]: prev[noteId].filter(c => c.id !== commentId)
      }));
      // Update comment count
      setNotes(notes.map(note => {
        if (note.id === noteId) {
          return { ...note, comments_count: Math.max(0, note.comments_count - 1) };
        }
        return note;
      }));
      toast.success('Comment deleted');
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-4xl mx-auto pb-24">
        {/* Header */}
        <div className="text-center mb-6 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#EFB342]/20 rounded-2xl mb-4">
            <Users className="text-[#EFB342]" size={32} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-2">COMMUNITY</h1>
          <p className="text-stone-600 text-base md:text-lg">Share wins, struggles, and support each other!</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <button
            onClick={() => setActiveTab('notes')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'notes'
                ? 'bg-[#EFB342] text-black'
                : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
            }`}
            data-testid="tab-notes"
          >
            <MessageCircle size={18} />
            Notes
          </button>
          <button
            onClick={() => setActiveTab('milestones')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'milestones'
                ? 'bg-[#EFB342] text-black'
                : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
            }`}
            data-testid="tab-milestones"
          >
            <Trophy size={18} />
            Group Wins
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'milestones' ? (
          <GroupMilestones />
        ) : (
          <>
            {/* New Note Input */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4 md:p-6 mb-6 animate-fade-in" data-testid="new-note-form">
              <div className="flex items-start gap-3 mb-3">
                <Sparkles className="text-[#EFB342] mt-1" size={20} />
                <p className="text-stone-700 text-base">Share today&apos;s win, or struggle, or just say hi!</p>
              </div>
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="What's on your mind?"
                className="bg-stone-100 border-stone-300 text-stone-900 text-base placeholder:text-stone-500 min-h-[100px] resize-none mb-3"
            data-testid="new-note-input"
          />
          
          {/* Image Preview */}
          {imageUrl && (
            <div className="relative mb-3 inline-block">
              <img 
                src={imageUrl} 
                alt="Upload preview" 
                className="max-h-32 rounded-lg border border-stone-300"
              />
              <button
                onClick={() => setImageUrl('')}
                className="absolute -top-2 -right-2 bg-red-500 text-stone-900 rounded-full p-1 hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </div>
          )}
          
          {/* Links Preview */}
          {links.length > 0 && (
            <div className="mb-3 space-y-2">
              {links.map((link, index) => (
                <div key={index} className="flex items-center gap-2 bg-stone-100 rounded-lg px-3 py-2 border border-stone-300">
                  <Link size={14} className="text-[#EFB342]" />
                  <span className="text-stone-700 text-sm flex-1 truncate">{link.label}</span>
                  <span className="text-stone-500 text-xs truncate max-w-[150px]">{link.url}</span>
                  <button
                    onClick={() => removeLink(index)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add Link Form */}
          {showLinkForm && (
            <div className="mb-3 p-3 bg-stone-100 rounded-lg border border-stone-300 space-y-2">
              <Input
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
                placeholder="Link label (e.g., 'Check out this recipe')"
                className="bg-white border-stone-300 text-stone-900 text-sm"
              />
              <Input
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                placeholder="URL (e.g., https://example.com)"
                className="bg-white border-stone-300 text-stone-900 text-sm"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={addLink}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                >
                  <Plus size={14} className="mr-1" /> Add Link
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setShowLinkForm(false); setNewLinkUrl(''); setNewLinkLabel(''); }}
                  className="text-stone-600 hover:text-stone-900"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              
              {/* Image Upload Button */}
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 text-stone-600 hover:text-[#EFB342] transition-colors text-sm"
                title="Add an image"
              >
                {uploading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Image size={18} />
                )}
                <span className="hidden sm:inline">Add Image</span>
              </button>
              
              {/* Add Link Button */}
              <button
                type="button"
                onClick={() => setShowLinkForm(!showLinkForm)}
                className="flex items-center gap-1.5 text-stone-600 hover:text-[#EFB342] transition-colors text-sm"
                title="Add a link"
              >
                <Link size={18} />
                <span className="hidden sm:inline">Add Link</span>
              </button>
            </div>
            
            <Button
              onClick={createNote}
              disabled={posting || (!newNote.trim() && !imageUrl)}
              className="bg-[#EFB342] text-black hover:bg-[#D99F30] text-base"
              data-testid="post-note-btn"
            >
              {posting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Posting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send size={16} />
                  Share
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Notes Feed */}
        <div className="space-y-4">
          {notes.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center">
              <MessageCircle className="text-stone-400 mx-auto mb-3" size={40} />
              <p className="text-stone-600">No notes yet. Be the first to share!</p>
            </div>
          ) : (
            notes.map((note) => {
              const isExpanded = expandedNotes.has(note.id);
              const comments = noteComments[note.id] || [];
              const canDelete = note.user_id === user?.id || user?.is_admin;
              
              return (
                <div 
                  key={note.id} 
                  className={`bg-white border rounded-2xl overflow-hidden animate-fade-in ${
                    note.is_pinned ? 'border-[#EFB342]/50 ring-1 ring-[#EFB342]/20' : 'border-stone-200'
                  }`}
                  data-testid={`note-${note.id}`}
                >
                  {/* Pinned Badge */}
                  {note.is_pinned && (
                    <div className="bg-[#EFB342]/10 px-4 py-1.5 flex items-center gap-2 border-b border-[#EFB342]/20">
                      <Pin size={12} className="text-[#EFB342]" />
                      <span className="text-[#EFB342] text-xs font-medium">Pinned</span>
                    </div>
                  )}
                  
                  {/* Note Content */}
                  <div className="p-4 md:p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#EFB342]/20 rounded-full flex items-center justify-center">
                          <span className="text-[#EFB342] font-bold text-base">
                            {note.user_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-stone-900 font-medium text-base">
                            {note.user_name}
                            {note.user_id === user?.id && (
                              <span className="text-sm text-[#EFB342]/70 ml-1">(You)</span>
                            )}
                          </p>
                          <p className="text-stone-500 text-sm flex items-center gap-1">
                            <Clock size={12} />
                            {formatTimeAgo(note.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Admin Pin Button */}
                        {isAdmin && (
                          <button
                            onClick={() => togglePinNote(note.id, note.is_pinned)}
                            disabled={pinningNote === note.id}
                            className={`p-1.5 rounded transition-colors ${
                              note.is_pinned 
                                ? 'text-[#EFB342] hover:text-[#EFB342]/70' 
                                : 'text-stone-500 hover:text-[#EFB342]'
                            }`}
                            title={note.is_pinned ? 'Unpin note' : 'Pin note to top'}
                            data-testid={`pin-note-${note.id}`}
                          >
                            {pinningNote === note.id ? (
                              <div className="w-4 h-4 border-2 border-[#EFB342]/30 border-t-[#EFB342] rounded-full animate-spin" />
                            ) : note.is_pinned ? (
                              <PinOff size={16} />
                            ) : (
                              <Pin size={16} />
                            )}
                          </button>
                        )}
                        {/* Delete Button */}
                        {canDelete && (
                          <button
                            onClick={() => deleteNote(note.id)}
                            className="text-stone-500 hover:text-red-400 p-1 rounded transition-colors"
                            title="Delete note"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-stone-800 text-base whitespace-pre-wrap break-words mb-4">{note.content}</p>
                    
                    {/* Note Image */}
                    {note.image_url && (
                      <div className="mb-4">
                        <img 
                          src={note.image_url} 
                          alt="Shared image" 
                          className="max-w-full rounded-lg border border-stone-300 max-h-96 object-contain"
                          onClick={() => window.open(note.image_url, '_blank')}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    )}
                    
                    {/* Note Links */}
                    {note.links && note.links.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {note.links.map((link, index) => (
                          <a
                            key={index}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg border border-stone-300 hover:border-[#EFB342]/50 transition-colors group"
                          >
                            <Link size={16} className="text-[#EFB342]" />
                            <span className="text-stone-800 group-hover:text-[#EFB342] transition-colors">{link.label}</span>
                            <span className="text-stone-500 text-xs ml-auto truncate max-w-[200px]">{link.url.replace(/^https?:\/\//, '')}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-4">
                      <div className="relative group">
                        <button
                          onClick={() => toggleReaction(note.id)}
                          onMouseEnter={() => note.reactions_count > 0 && fetchReactionNames(note.id)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-base ${
                            note.user_reacted
                              ? 'bg-[#EFB342]/20 text-[#EFB342]'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                          data-testid={`react-note-${note.id}`}
                        >
                          <span role="img" aria-label="fist bump">👊</span>
                          <span>{note.reactions_count || 0}</span>
                        </button>
                        
                        {/* Tooltip showing who reacted */}
                        {note.reactions_count > 0 && (
                          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 whitespace-nowrap">
                            {loadingReactions[note.id] ? (
                              <span className="text-xs text-stone-600">Loading...</span>
                            ) : reactionNames[note.id]?.length > 0 ? (
                              <div className="text-xs text-stone-700">
                                <span className="text-[#EFB342] font-medium">Fist bumps from:</span>
                                <div className="mt-1">
                                  {reactionNames[note.id].slice(0, 10).join(', ')}
                                  {reactionNames[note.id].length > 10 && ` +${reactionNames[note.id].length - 10} more`}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-stone-600">Hover to see who reacted</span>
                            )}
                            <div className="absolute top-full left-4 border-4 border-transparent border-t-zinc-800"></div>
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={() => toggleExpand(note.id)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all text-base"
                        data-testid={`toggle-comments-${note.id}`}
                      >
                        <MessageCircle size={16} />
                        <span>{note.comments_count || 0}</span>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Comments Section */}
                  {isExpanded && (
                    <div className="border-t border-stone-200 bg-stone-50/50 p-4">
                      {loadingComments.has(note.id) ? (
                        <div className="flex justify-center py-4">
                          <div className="w-5 h-5 border-2 border-[#EFB342]/30 border-t-[#EFB342] rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        <>
                          {/* Existing Comments */}
                          {comments.length > 0 && (
                            <div className="space-y-3 mb-4">
                              {comments.map((comment) => {
                                const canDeleteComment = comment.user_id === user?.id || user?.is_admin;
                                return (
                                  <div key={comment.id} className="flex gap-2 group" data-testid={`comment-${comment.id}`}>
                                    <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-stone-600 font-medium text-sm">
                                        {comment.user_name?.charAt(0)?.toUpperCase() || '?'}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="bg-stone-50 rounded-lg px-3 py-2">
                                        <p className="text-stone-900 text-sm font-medium">{comment.user_name}</p>
                                        <p className="text-stone-700 text-base break-words">{comment.content}</p>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="text-stone-400 text-sm">{formatTimeAgo(comment.created_at)}</span>
                                        {canDeleteComment && (
                                          <button
                                            onClick={() => deleteComment(note.id, comment.id)}
                                            className="text-stone-400 hover:text-red-400 text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                          >
                                            Delete
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Add Comment Input */}
                          <div className="flex gap-2">
                            <div className="w-8 h-8 bg-[#EFB342]/20 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-[#EFB342] font-medium text-sm">
                                {user?.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="flex-1 flex gap-2">
                              <input
                                type="text"
                                value={commentInputs[note.id] || ''}
                                onChange={(e) => setCommentInputs(prev => ({
                                  ...prev,
                                  [note.id]: e.target.value
                                }))}
                                placeholder="Write a comment..."
                                className="flex-1 bg-stone-100 border border-stone-300 rounded-full px-4 py-2 text-base text-stone-900 placeholder:text-stone-500 focus:outline-none focus:border-[#EFB342]/50"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    addComment(note.id);
                                  }
                                }}
                                data-testid={`comment-input-${note.id}`}
                              />
                              <Button
                                onClick={() => addComment(note.id)}
                                disabled={postingComment === note.id || !commentInputs[note.id]?.trim()}
                                size="sm"
                                className="bg-[#EFB342] text-black hover:bg-[#D99F30] rounded-full px-3"
                                data-testid={`post-comment-${note.id}`}
                              >
                                {postingComment === note.id ? (
                                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                ) : (
                                  <Send size={14} />
                                )}
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Load More - TODO: Implement pagination */}
        {notes.length >= 20 && (
          <div className="text-center mt-6">
            <Button variant="outline" className="border-stone-300 text-stone-600 hover:bg-stone-100">
              Load More
            </Button>
          </div>
        )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Community;
